(() => {
  const DEFAULT_DASHBOARD_ENDPOINT = '/apps/pokeplug/account/dashboard';

  class PlugAccountHub {
    constructor(root) {
      this.root = root;
      this.app = root.querySelector('.\\#plug-account__app');
      this.sidebar = root.querySelector('[data-account-sidebar]');
      this.menuButton = root.querySelector('[data-account-menu]');
      this.authBanner = root.querySelector('[data-account-auth-banner]');
      this.apiState = root.querySelector('[data-account-api-state]');
      this.loginAction = root.querySelector('[data-api-login-action]');
      this.retryAction = root.querySelector('[data-api-retry-action]');
      this.stateEyebrow = root.querySelector('[data-api-state-eyebrow]');
      this.stateHeading = root.querySelector('[data-api-state-heading]');
      this.stateText = root.querySelector('[data-api-state-text]');
      this.hasFetched = false;
      this.dashboardEndpoint =
        root.dataset.apiUrl ||
        DEFAULT_DASHBOARD_ENDPOINT;
    }

    init() {
      this.menuButton?.addEventListener('click', () => {
        this.sidebar?.classList.toggle('is-open');
      });

      this.root.querySelectorAll('.\\#plug-account__nav a').forEach((link) => {
        link.addEventListener('click', () => {
          this.sidebar?.classList.remove('is-open');
        });
      });

      this.root.querySelectorAll('[data-account-module]').forEach((module) => {
        module.addEventListener('click', (event) => {
          if (module.getAttribute('href') === '#') {
            event.preventDefault();
          }
        });
      });

      this.retryAction?.addEventListener('click', () => {
        this.loadDashboard({ force: true });
      });

      this.loadDashboard();
    }

    async loadDashboard({ force = false } = {}) {
      if (this.hasFetched && !force) return;

      this.hasFetched = true;
      this.app?.classList.add('is-loading');
      this.app?.classList.remove('has-dashboard-data');
      this.hideApiState();

      try {
        const response = await fetch(this.dashboardEndpoint);
        const responseText = await response.text();

        let payload = null;

        try {
          payload = JSON.parse(responseText);
        } catch (parseError) {
          console.error(
            'PokePlug dashboard returned non-JSON content.',
            {
              status: response.status,
              contentType: response.headers.get('content-type'),
              preview: responseText.slice(0, 240)
            }
          );

          this.showErrorState(
            'The dashboard endpoint returned an invalid response. Please try again.'
          );
          return;
        }

        console.info('PokePlug dashboard response', {
          endpoint: this.dashboardEndpoint,
          status: response.status,
          authenticated:
            payload?.authenticated ??
            payload?.data?.authenticated ??
            payload?.dashboard?.authenticated,
          error:
            payload?.error === true ||
            payload?.data?.error === true
        });

        if (response.status === 403) {
          this.showErrorState(
            payload?.message ||
              this.root.dataset.apiProxyErrorText ||
              'The storefront could not verify the PokePlug app-proxy request.'
          );
          return;
        }

        const payloadError =
          payload?.error === true ||
          payload?.data?.error === true;

        if (!response.ok || payloadError) {
          this.showErrorState(
            payload?.message ||
              payload?.data?.message ||
              this.root.dataset.apiErrorText ||
              'Dashboard data is temporarily unavailable.'
          );
          return;
        }

        const authenticated =
          payload?.authenticated ??
          payload?.data?.authenticated ??
          payload?.dashboard?.authenticated;

        if (authenticated === false) {
          this.showLoginState();
          return;
        }

        this.hideLoginState();

        const dashboardData =
          payload?.dashboard ||
          payload?.data?.dashboard ||
          payload?.data ||
          payload?.result ||
          payload ||
          {};

        this.applyData(dashboardData);
      } catch (error) {
        console.error('PokePlug account dashboard request failed.', error);

        this.showErrorState(
          this.root.dataset.apiErrorText ||
            'Dashboard data is temporarily unavailable.'
        );
      } finally {
        this.app?.classList.remove('is-loading');
      }
    }

    showLoginState() {
      this.setStateCopy({
        eyebrow: 'Trainer access',
        heading: 'Please log in',
        text:
          'Sign in to load your PokePlug dashboard, orders, Vault, Pack Rips, Trade-Ins, Buy Lists and rewards.'
      });

      this.loginAction?.removeAttribute('hidden');
      this.retryAction?.setAttribute('hidden', '');
      this.apiState?.removeAttribute('hidden');
      this.app?.classList.add('is-signed-out');
      this.authBanner?.setAttribute('hidden', '');
    }

    showErrorState(message) {
      this.setStateCopy({
        eyebrow:
          this.root.dataset.apiErrorEyebrow ||
          'Temporary issue',
        heading:
          this.root.dataset.apiErrorHeading ||
          'Dashboard data is unavailable',
        text: message
      });

      this.loginAction?.setAttribute('hidden', '');
      this.retryAction?.removeAttribute('hidden');
      this.apiState?.removeAttribute('hidden');
      this.app?.classList.add('has-api-error');
      this.authBanner?.setAttribute('hidden', '');
    }

    hideApiState() {
      this.apiState?.setAttribute('hidden', '');
      this.app?.classList.remove('has-api-error');
    }

    hideLoginState() {
      this.apiState?.setAttribute('hidden', '');
      this.app?.classList.remove('is-signed-out', 'has-api-error');
      this.app?.classList.add('is-api-authenticated');
      this.authBanner?.setAttribute('hidden', '');
    }

    applyData(data) {
      const customer = data.customer || {};

      this.setText(
        '[data-account-customer-name]',
        this.displayValue(customer.name, 'Trainer')
      );
      this.setText(
        '[data-account-customer-email]',
        this.displayValue(customer.email, '')
      );

      this.setText(
        '[data-account-level]',
        this.displayValue(data.level, '—')
      );
      this.setText(
        '[data-account-rank]',
        this.displayValue(data.rank, 'New Trainer')
      );
      this.setText(
        '[data-account-xp-label]',
        this.displayValue(data.xpLabel, '0 XP')
      );

      this.setText(
        '[data-account-points]',
        this.numericValue(data.points)
      );
      this.setText(
        '[data-account-orders]',
        this.numericValue(data.ordersCount)
      );
      this.setText(
        '[data-account-total-spent]',
        this.displayValue(data.totalSpent, '$0.00')
      );
      this.setText(
        '[data-account-cards]',
        this.numericValue(data.cardsOwned)
      );
      this.setText(
        '[data-account-vault-value]',
        this.displayValue(data.vaultValue, '$0.00')
      );

      this.setText(
        '[data-account-vault-count]',
        this.displayValue(data.vaultCountLabel, 'No cards stored yet')
      );
      this.setText(
        '[data-account-pack-count]',
        this.displayValue(data.packRipCountLabel, 'No packs ripped yet')
      );
      this.setText(
        '[data-account-trade-count]',
        this.displayValue(data.tradeInCountLabel, 'No active trade-ins')
      );
      this.setText(
        '[data-account-buy-count]',
        this.displayValue(data.buyListCountLabel, 'No buy-list submissions')
      );

      this.setText('[data-account-vault-status]', 'Live');
      this.setText('[data-account-pack-status]', 'Live');
      this.setText('[data-account-trade-status]', 'Live');
      this.setText('[data-account-buy-status]', 'Live');
      this.setText('[data-account-points-state]', 'Live');
      this.setText('[data-account-vault-value-state]', 'Live');

      const progress = Number(data.xpProgress);
      const clampedProgress = Number.isFinite(progress)
        ? Math.max(0, Math.min(100, progress))
        : 0;

      const bar = this.root.querySelector('[data-account-xp-bar]');
      if (bar) {
        bar.style.width = `${clampedProgress}%`;
        bar.setAttribute('aria-valuenow', String(clampedProgress));
      }

      this.root.querySelectorAll('[data-account-module]').forEach((module) => {
        module.classList.remove('is-building');
        module.classList.add('is-live');
      });

      this.app?.classList.add('has-dashboard-data');
      this.app?.classList.remove('is-loading');
    }

    setStateCopy({ eyebrow, heading, text }) {
      if (this.stateEyebrow) this.stateEyebrow.textContent = eyebrow;
      if (this.stateHeading) this.stateHeading.textContent = heading;
      if (this.stateText) this.stateText.textContent = text;
    }

    numericValue(value) {
      if (value === null || value === undefined || value === '') {
        return '0';
      }

      const number = Number(value);
      return Number.isFinite(number) ? String(number) : '0';
    }

    displayValue(value, fallback) {
      if (value === null || value === undefined || value === '') {
        return fallback;
      }

      return String(value);
    }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach((element) => {
        element.textContent = String(value);
      });
    }
  }

  document.querySelectorAll('[data-plug-account]').forEach((root) => {
    new PlugAccountHub(root).init();
  });
})();